import { useState, useEffect } from 'react'
import { Refrigerator, Flame, Sparkles, Microwave, WashingMachine, Wind, Droplets, Thermometer, HelpCircle } from 'lucide-react'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', brown: '#2C1D14', muted: '#9A7B68',
  ghostBg: '#FBF6EE', // lighter than documented card so it still reads as "incomplete"
}
const f = 'Georgia, serif'
const fs = 'Calibri, sans-serif'

// Canonical slot definitions.
// applianceType is what we pass to AddAppliance.initialType to pre-select the type card.
// matchTypes is what we look for in appliances.type to decide whether the slot is already filled.
const SLOTS = [
  { key: 'refrigerator', label: 'Refrigerator',  applianceType: 'refrigerator', matchTypes: ['refrigerator'],   Icon: Refrigerator },
  { key: 'range_oven',   label: 'Range / Oven',  applianceType: 'oven',         matchTypes: ['range', 'oven'],  Icon: Flame },
  { key: 'dishwasher',   label: 'Dishwasher',    applianceType: 'dishwasher',   matchTypes: ['dishwasher'],     Icon: Sparkles },
  { key: 'microwave',    label: 'Microwave',     applianceType: 'microwave',    matchTypes: ['microwave'],      Icon: Microwave },
  { key: 'washer',       label: 'Washer',        applianceType: 'washer',       matchTypes: ['washer'],         Icon: WashingMachine },
  { key: 'dryer',        label: 'Dryer',         applianceType: 'dryer',        matchTypes: ['dryer'],          Icon: Wind },
  { key: 'water_heater', label: 'Water Heater',  applianceType: 'water_heater', matchTypes: ['water_heater'],   Icon: Droplets },
  { key: 'hvac',         label: 'HVAC',          applianceType: 'hvac',         matchTypes: ['hvac'],           Icon: Thermometer },
]

const S = {
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '12px', marginBottom: '20px',
  },
  ghostCard: {
    background: C.ghostBg, border: `2px dashed ${C.amber}`,
    borderRadius: '12px', padding: '20px 16px',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    minHeight: '180px', textAlign: 'center',
    cursor: 'pointer', transition: 'background 0.15s, box-shadow 0.15s',
  },
  notDocumented: {
    fontSize: '13px', color: C.amber, fontFamily: fs, fontWeight: '700',
    letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: '6px',
  },
  titleRow: {
    position: 'relative',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '6px', marginBottom: '14px',
  },
  title: { fontSize: '18px', fontWeight: '600', color: C.amber, fontFamily: f },
  helpBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: 'transparent', border: 'none', padding: '2px', margin: 0,
    cursor: 'pointer', color: C.muted, lineHeight: 0,
  },
  tooltip: {
    position: 'absolute', top: 'calc(100% + 6px)', left: '50%',
    transform: 'translateX(-50%)', zIndex: 10,
    background: C.card, border: `1px solid ${C.border}`,
    borderRadius: '8px', padding: '8px 10px',
    fontSize: '12px', fontFamily: fs, color: C.brown,
    boxShadow: '0 4px 12px rgba(44,29,20,0.12)',
    whiteSpace: 'nowrap',
  },
  actions: { width: '100%', display: 'flex', flexDirection: 'column', gap: '6px', marginTop: 'auto' },
  primaryBtn: {
    width: '100%', fontSize: '12px', color: C.amber, fontFamily: fs, fontWeight: '700',
    border: `1px solid ${C.amber}`, borderRadius: '6px',
    padding: '7px 10px', background: 'transparent', cursor: 'pointer',
  },
  sectionHeader: { marginBottom: '12px' },
  sectionTitle: { fontSize: '15px', fontWeight: '700', color: C.brown, fontFamily: f, marginBottom: '2px' },
  sectionSub: { fontSize: '12px', color: C.muted, fontFamily: fs, lineHeight: 1.4 },
}

export default function StandardAppliancesPrompt({ appliances = [], dismissedKeys = [], onAdd, onDismiss }) {
  const documentedTypes = new Set((appliances || []).map(a => (a.type || '').toLowerCase()))
  const dismissed = new Set(dismissedKeys || [])

  const visible = SLOTS.filter(slot => {
    if (dismissed.has(slot.key)) return false
    if (slot.matchTypes.some(t => documentedTypes.has(t))) return false
    return true
  })

  // Tooltip state — `stickyKey` is set by click (mobile tap, desktop click) and
  // dismissed by an outside click. `hoverKey` is the ephemeral desktop-hover state.
  // Tooltip is visible for a slot if either matches its key.
  const [stickyKey, setStickyKey] = useState(null)
  const [hoverKey, setHoverKey] = useState(null)

  useEffect(() => {
    if (!stickyKey) return
    const handler = (e) => {
      if (e.target.closest('.casavio-stdslot-tooltip')) return
      setStickyKey(null)
    }
    // Defer one tick so the click that opened the tooltip doesn't immediately close it.
    const t = setTimeout(() => document.addEventListener('click', handler), 0)
    return () => { clearTimeout(t); document.removeEventListener('click', handler) }
  }, [stickyKey])

  if (visible.length === 0) return null

  return (
    <div style={{ marginBottom: '20px' }}>
      <style>{`
        .casavio-stdslot-dismiss {
          width: 100%;
          font-size: 11px;
          color: ${C.muted};
          font-family: ${fs};
          font-weight: 400;
          border: 1px solid ${C.border};
          border-radius: 6px;
          padding: 5px 10px;
          background: transparent;
          cursor: pointer;
          transition: border-color 0.15s;
        }
        .casavio-stdslot-dismiss:hover {
          border-color: ${C.muted};
        }
        .casavio-stdslot-help {
          opacity: 0.55;
          transition: opacity 0.15s;
        }
        .casavio-stdslot-help:hover,
        .casavio-stdslot-help:focus-visible {
          opacity: 1;
          outline: none;
        }
        .casavio-stdslot-card:hover {
          background: #F5EEDF;
          box-shadow: 0 2px 8px rgba(184,114,31,0.15);
        }
        .casavio-stdslot-card:focus-visible {
          outline: 2px solid ${C.amber};
          outline-offset: 2px;
        }
      `}</style>
      <div style={S.sectionHeader}>
        <div style={S.sectionTitle}>Complete your home inventory</div>
        <div style={S.sectionSub}>Common appliances you haven't documented yet — tap to add, or dismiss if you don't have one.</div>
      </div>
      <div style={S.grid}>
        {visible.map(slot => {
          const SIcon = slot.Icon
          const openAdd = () => onAdd && onAdd(slot.applianceType)
          return (
            <div
              key={slot.key}
              className="casavio-stdslot-card"
              style={S.ghostCard}
              role="button"
              tabIndex={0}
              aria-label={`Add ${slot.label}`}
              onClick={openAdd}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  openAdd()
                }
              }}
            >
              <div style={{ marginBottom: '10px' }}>
                <SIcon size={32} strokeWidth={1.5} color={C.amber} />
              </div>
              <div style={S.notDocumented}>Not documented</div>
              <div style={S.titleRow}>
                <span style={S.title}>{slot.label}</span>
                <button
                  type="button"
                  className="casavio-stdslot-help"
                  style={S.helpBtn}
                  aria-label="Why complete this?"
                  onMouseEnter={() => setHoverKey(slot.key)}
                  onMouseLeave={() => setHoverKey(prev => prev === slot.key ? null : prev)}
                  onClick={(e) => {
                    e.stopPropagation()
                    setStickyKey(prev => prev === slot.key ? null : slot.key)
                  }}
                >
                  <HelpCircle size={16} strokeWidth={2} />
                </button>
                {(stickyKey === slot.key || hoverKey === slot.key) && (
                  <div className="casavio-stdslot-tooltip" style={S.tooltip} role="tooltip">
                    Improves your Home Health Score
                  </div>
                )}
              </div>
              <div style={S.actions}>
                <button
                  type="button"
                  style={S.primaryBtn}
                  onClick={(e) => { e.stopPropagation(); openAdd() }}
                >
                  + Scan or add
                </button>
                <button
                  type="button"
                  className="casavio-stdslot-dismiss"
                  onClick={(e) => { e.stopPropagation(); onDismiss && onDismiss(slot.key) }}
                >
                  I don't have this
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
